/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import NButton from '../../components/inputs/NButton';
import useProjectAPI, { useProjectHook } from '../../hooks/network/useProjectAPI';
import { RouteEnums } from '../../Routes/RoutesConstant';
import NSearch from '../../components/inputs/NSearch';
import NTable2 from '../../components/table/NTable2';
import useCustomerAPI from '../../hooks/network/useCustomerAPI';
import MobileEdit from '../../assets/svg/MobileEdit';
import MobileDelete from '../../assets/svg/MobileDelete';
import { successToaster } from '../../components/toaster/Toaster';
import NSearchableDropDown from '../../components/inputs/NSearchableDropdown';

function ManageProjects() {
  const navigate = useNavigate();
  const [customerOptions, setCustomerOptions] = useState();
  const { deleteProject } = useProjectAPI();
  const { getCustomers } = useCustomerAPI();

  const {
    lastElementRef,
    projects,
    isPaginationLoading,
    searchString,
    setSearchString,
    setCustomerId,
    customerId,
    fetchProjects,
    setProjects,
    setPage,
  } = useProjectHook();

  useEffect(() => {
    fetchCustomerOptions();
  }, []);

  const columns = [
    {
      width: '20%',
      key: 'customer',
      title: 'Customer',
      isSortable: false,
      rowRender: (rowData) => (
        <p className='min-w-[170px]'>
          <span> {rowData?.customer} </span>
        </p>
      ),
    },
    {
      width: '100%',
      key: 'projectName',
      title: 'Project',
      isSortable: true,
      rowRender: (rowData) => (
        <p className='min-w-[170px]'>
          <span> {rowData?.projectName} </span>{' '}
        </p>
      ),
    },
    {
      width: '10%',
      key: 'startDate',
      title: 'Start Date',
      isSortable: false,
      rowRender: (rowData) => <span> {moment(rowData?.startDate).format('DD/MM/YYYY')} </span>,
    },
    {
      width: '10%',
      key: 'endDate',
      title: 'End Date',
      isSortable: false,
      rowRender: (rowData) => <span> {moment(rowData?.endDate).format('DD/MM/YYYY')} </span>,
    },
    {
      width: '10%',
      key: 'personInCharge',
      title: 'Project InCharge',
      headerRender: () => (
        <>
          Project <br /> InCharge
        </>
      ),
      isSortable: false,
      rowRender: (rowData) => (
        <span className='max-w-[70px] break-all'> {rowData?.personInCharge} </span>
      ),
    },
    {
      width: '15%',
      key: 'personInChargeEmail',
      title: 'Project InCharge Email',
      headerRender: () => (
        <>
          Project <br /> InCharge Email
        </>
      ),
      isSortable: false,
      rowRender: (rowData) => (
        <p className=''>
          <span className='max-w-[70px] break-all'> {rowData?.personInChargeEmail} </span>
        </p>
      ),
    },
    {
      width: '10%',
      key: 'sapid',
      title: 'Project ID (SAP)',
      isSortable: false,
      rowRender: (rowData) => <span> {rowData?.sapid} </span>,
    },
    {
      width: '15%',
      key: 'actions',
      title: 'Actions',
      columClassName: 'flex justify-end',
      rowRender: (rowData) => (
        <div>
          {/* Mobile */}
          <div className='flex items-center justify-end gap-6 break:hidden'>
            <div onClick={() => onClickEdit(rowData?.id)}>
              <MobileEdit />
            </div>
            <div onClick={() => onClickDelete(rowData?.id)}>
              <MobileDelete />
            </div>
          </div>

          {/* Desktop */}
          <div className='hidden gap-4 break:flex'>
            <NButton
              className='action-edit-btn'
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => onClickEdit(rowData?.id)}
            >
              Edit
            </NButton>
            <NButton
              className='action-delete-btn'
              variant='outlined'
              startIcon={<DeleteIcon />}
              onClick={() => onClickDelete(rowData?.id)}
            >
              Delete
            </NButton>
          </div>
        </div>
      ),
    },
  ];

  const fetchCustomerOptions = async () => {
    try {
      const response = await getCustomers();
      setCustomerOptions(response);
    } catch {}
  };

  const onClickEdit = (id) => {
    navigate(`${RouteEnums.PROJECT_EDIT}/${id}`);
  };

  const onClickDelete = (id) => {
    const response = window.confirm('Press ok to delete project?');
    if (response) handleDeleteProject(id);
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const response = await deleteProject(projectId);
      if (!response) return;
      successToaster(response?.message || 'Deleted Successfully');
      fetchProjects();
    } catch {}
  };

  const getCustomerOptions = () => {
    if (customerOptions?.length) {
      return customerOptions?.map((o) => ({
        label: o.customerName,
        value: o.id?.toString(),
      }));
    }
    return [];
  };

  const optionValueCustomer = (cusVal) => {
    const selectedObj = customerOptions?.find((o) => o?.id?.toString() === cusVal?.toString());
    return { label: selectedObj?.customerName, value: selectedObj?.id?.toString() };
  };

  return (
    <section className='n-table-wrapper'>
      {/* Mobile Header */}
      <div className='break:hidden'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='font-semibold'>Projects</h2>
          <div>
            <div>
              <NButton variant='Noutline' onClick={() => navigate(RouteEnums.PROJECT_ADD)}>
                Add Project
              </NButton>
            </div>
          </div>
        </div>
        <div className='mb-4'>
          <NSearch value={searchString} onChange={(e) => setSearchString(e?.target?.value)} />
        </div>
      </div>

      {/* Desktop Header */}
      <div className='mb-10 mt-4 hidden flex-col items-start justify-between gap-5 break:flex break:gap-10 xxl:flex-row xxl:items-center'>
        <div className='flex w-full flex-row gap-4 break:gap-8'>
          <div className='w-full xxl:w-auto'>
            <NSearch value={searchString} onChange={(e) => setSearchString(e?.target?.value)} />
          </div>
          <div className='w-full xxl:w-4/12'>
            {/* <NDropdown
              label='Customer Name*'
              name='customer'
              value={customerId}
              onChange={(e) => {
                setProjects(undefined);
                setPage(1);
                setCustomerId(e?.target?.value);
              }}
              className='w-full !bg-white'
              type='text'
              options={[{ label: 'Select Customer', value: '' }, ...getCustomerOptions()]}
            /> */}
            <NSearchableDropDown
              label='Customer Name*'
              name='customer'
              value={optionValueCustomer(customerId)}
              onChange={(e, selectedValue) => {
                setProjects(undefined);
                setPage(1);
                setCustomerId(selectedValue?.value);
              }}
              className='w-full !bg-white'
              type='text'
              options={[{ label: 'Select Customer', value: '' }, ...getCustomerOptions()]}
            />
          </div>
        </div>
        <div className='flex justify-start xxl:w-3/12 xxl:justify-end'>
          <NButton
            className='!bg-nblue text-nblue'
            onClick={() => navigate(RouteEnums.PROJECT_ADD)}
          >
            Add Project
          </NButton>
        </div>
      </div>

      <div className={`${projects?.length ? 'ntable-container' : ''}`}>
        <NTable2
          isLoading={isPaginationLoading}
          rows={projects || []}
          columns={columns}
          lastElementRef={lastElementRef}
        />
      </div>
    </section>
  );
}

ManageProjects.propTypes = {};

export default ManageProjects;
